import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Send, Sparkles, Trash2, Copy, RotateCcw, Bot, User as UserIcon, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

const SESSION_KEY = "coomflona_railan_session";

function newSessionId() {
  return `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

const SUGGESTIONS = [
  "Escreva uma convocação para assembleia geral extraordinária no dia 15/10.",
  "Como funciona o limite de 30% de adiantamento no Estatuto?",
  "Redija um ofício para o INSS solicitando informações sobre benefício previdenciário de um cooperado.",
  "Crie uma declaração de que o cooperado está regularmente matriculado na cooperativa desde 2010.",
];

export default function RailanChat() {
  const [sessionId, setSessionId] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = newSessionId();
      localStorage.setItem(SESSION_KEY, sid);
    }
    setSessionId(sid);
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    (async () => {
      setLoadingHistory(true);
      try {
        const { data } = await api.get(`/secretariat/railan/history?session_id=${sessionId}`);
        setMessages(data || []);
      } catch (err) {
        // sem histórico é ok
      } finally {
        setLoadingHistory(false);
        setTimeout(() => scrollToBottom(), 100);
      }
    })();
  }, [sessionId]);

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  const send = async (textOverride) => {
    const text = (textOverride || input).trim();
    if (!text || sending) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text, id: `local-${Date.now()}` }]);
    setSending(true);
    setTimeout(scrollToBottom, 50);

    try {
      const { data } = await api.post("/secretariat/railan/chat", {
        session_id: sessionId,
        message: text,
      });
      setMessages((prev) => [...prev, data.message]);
    } catch (err) {
      toast.error(formatApiError(err));
      setMessages((prev) => prev.slice(0, -1));
      setInput(text);
    } finally {
      setSending(false);
      setTimeout(scrollToBottom, 50);
    }
  };

  const startNewChat = async () => {
    if (messages.length && !window.confirm("Iniciar uma nova conversa? O histórico atual será apagado.")) return;
    try {
      await api.delete(`/secretariat/railan/history?session_id=${sessionId}`);
    } catch (err) {
      // ignora
    }
    const sid = newSessionId();
    localStorage.setItem(SESSION_KEY, sid);
    setSessionId(sid);
    setMessages([]);
    toast.success("Nova conversa iniciada.");
  };

  const copyMessage = async (content) => {
    try {
      await navigator.clipboard.writeText(content);
      toast.success("Texto copiado!");
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  return (
    <div className="mt-8" data-testid="railan-chat">
      <div className="bg-forest-900 text-cream-100 p-5 mb-6 relative overflow-hidden">
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-copaiba-500/10 blur-2xl" aria-hidden />
        <div className="flex items-start justify-between gap-4 relative">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-copaiba-500/20 border-2 border-copaiba-300 flex items-center justify-center shrink-0">
              <Sparkles className="w-6 h-6 text-copaiba-300" />
            </div>
            <div>
              <div className="font-display text-2xl leading-tight">Railan</div>
              <div className="text-xs uppercase tracking-[0.18em] text-copaiba-300">Assistente da Secretaria · IA interna</div>
              <p className="text-cream-100/80 text-sm mt-2 max-w-xl leading-relaxed">
                Pergunte sobre a COOMFLONA ou peça para criar qualquer documento oficial. O Railan responde formal e
                pronto pra colar no site.
              </p>
            </div>
          </div>
          <Button
            onClick={startNewChat}
            variant="ghost"
            className="text-cream-100 hover:bg-cream-100/10 rounded-none border border-cream-100/20"
            data-testid="railan-new-chat"
            title="Iniciar nova conversa"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Nova conversa
          </Button>
        </div>
      </div>

      {/* Área de mensagens */}
      <div
        ref={scrollRef}
        className="bg-cream-100 border border-cream-400 h-[520px] overflow-y-auto p-5 space-y-4"
        data-testid="railan-messages"
      >
        {loadingHistory ? (
          <div className="h-full flex items-center justify-center text-forest-900/50">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-6">
            <div className="w-16 h-16 rounded-full bg-forest-700 flex items-center justify-center mb-4">
              <Bot className="w-8 h-8 text-copaiba-300" />
            </div>
            <div className="font-display text-2xl text-forest-900 mb-2">Como posso ajudar?</div>
            <p className="text-forest-900/70 text-sm max-w-md mb-6 leading-relaxed">
              Estou aqui pra tirar dúvidas e redigir qualquer documento oficial da COOMFLONA. Experimente uma das
              sugestões abaixo:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl w-full">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => send(s)}
                  disabled={sending}
                  className="text-left bg-white border border-cream-400 p-3 text-sm text-forest-900 hover:border-forest-700 hover:bg-cream-200 transition-colors leading-snug"
                  data-testid={`railan-suggestion-${i}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m, i) => (
            <MessageBubble key={m.id || i} message={m} onCopy={copyMessage} />
          ))
        )}

        {sending && (
          <div className="flex items-center gap-3" data-testid="railan-typing">
            <div className="w-8 h-8 rounded-full bg-forest-700 flex items-center justify-center shrink-0">
              <Bot className="w-4 h-4 text-copaiba-300" />
            </div>
            <div className="bg-white border border-cream-400 px-4 py-3 text-sm text-forest-900/70">
              <span className="inline-flex gap-1">
                <span className="w-1.5 h-1.5 bg-forest-700 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 bg-forest-700 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 bg-forest-700 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="bg-white border border-t-0 border-cream-400 p-4">
        <div className="flex items-end gap-3">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Escreva sua pergunta ou peça para o Railan redigir um documento..."
            rows={2}
            disabled={sending}
            className="flex-1 resize-none"
            data-testid="railan-input"
          />
          <Button
            onClick={() => send()}
            disabled={!input.trim() || sending}
            className="bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none h-[68px] px-6"
            data-testid="railan-send"
          >
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </Button>
        </div>
        <div className="text-xs text-forest-900/50 mt-2">
          Enter envia · Shift+Enter quebra linha · IA interna da COOMFLONA — nunca envie dados sigilosos que você
          não repassaria a um colega da secretaria.
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ message, onCopy }) {
  const isUser = message.role === "user";
  const isAssistant = message.role === "assistant";
  return (
    <div className={`flex items-start gap-3 ${isUser ? "flex-row-reverse" : ""}`}>
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
          isUser ? "bg-copaiba-500 text-forest-900" : "bg-forest-700 text-copaiba-300"
        }`}
      >
        {isUser ? <UserIcon className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
      </div>
      <div className={`max-w-[85%] group ${isUser ? "text-right" : ""}`}>
        <div
          className={`inline-block text-left px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
            isUser
              ? "bg-forest-700 text-cream-100"
              : "bg-white border border-cream-400 text-forest-900"
          }`}
          data-testid={`railan-msg-${message.role}`}
        >
          {message.content}
        </div>
        {isAssistant && (
          <div className="mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => onCopy(message.content)}
              className="text-xs text-forest-900/60 hover:text-forest-900 inline-flex items-center gap-1"
              data-testid="railan-copy-msg"
            >
              <Copy className="w-3 h-3" /> Copiar resposta
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
